import { SingingPortraitFragment } from 'api/contentful/contentful.graphql.types'
import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import {
  ContentfulMediaMentions,
  getContentfulMediaMentions,
} from 'api/contentful/getContentfulMediaMention'
import { getContentfulSingingPortraits } from 'api/contentful/memory'
import { Block } from 'components/Block'
import { HelpCarousel } from 'components/content/helpCarousel/HelpCarousel'
import { HomepageCallToAction } from 'components/content/homepage/homepageCallToAction/HomepageCallToAction'
import { LifeStoriesContextProvider } from 'components/content/lifeStories/LifeStoriesContext'
import { MediaMentionsRow } from 'components/content/mediaMentions/MediaMentionsRow'
import { singingPortraitsHomepageImages } from 'components/content/singingPortraits/content'
import { SingingPortraitsHero } from 'components/content/singingPortraits/singingPortraitsHero/SingingPortraitsHero'
import { SingingPortraitsWizardCards } from 'components/content/singingPortraits/singingPortraitsWizardCards/SingingPortraitsWizardCards'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Layout } from 'components/layout/Layout'

import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { Spacer } from 'components/spacer/Spacer'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'
import { mutateAnalyticsData } from 'utils/analytics'
import { REVALIDATE_SECS } from 'utils/constants'
import { getIntlProps } from 'utils/i18n/getIntlProps'

export default function Page(props: {
  singingPortraits: SingingPortraitFragment[]
  mediaMentions: ContentfulMediaMentions
  faq: ContentfulFAQ
}) {
  const { t } = useTranslation('memory')
  useEffect(() => {
    mutateAnalyticsData({ page: 'singing_landing' })
  }, [])
  return (
    <LifeStoriesContextProvider>
      <Layout
        header={<TopNavigationLandingPage variant="full" />}
        footer={<Footer variant="full" />}
      >
        <PageTitle title={t('memory.singing.meta_title')} />

        <PageMeta
          description={t('memory.singing.meta_desc')}
          image="/images/preview/singing-portraits.png"
        />

        <SingingPortraitsHero
          previewAnimations={props.singingPortraits}
          title={t('memory.singing.title')}
          desc={t('memory.singing.desc')}
        />

        <Spacer size={[2, 0.5]} />

        <Block>
          <MediaMentionsRow
            mediaMentions={props.mediaMentions}
            title={t('memory.singing.media_mention_title')}
            hideQuotes
            alignCenterItems
            logoMaxHeightInRem={3}
            limit={5}
          />
        </Block>

        <Spacer size={[4, 6]} />

        <SingingPortraitsWizardCards />

        <Spacer size={[4, 6]} />

        {/*IMPROVE: [Localization] This is not translated*/}
        <HelpCarousel />

        <Spacer size={[5, 8]} />

        <FaqTwoColumns content={props.faq} />

        <Spacer size={[5, 9]} />

        <HomepageCallToAction
          mirrored
          actionTitle={t('memory.singing.call_to_action.title')}
          images={singingPortraitsHomepageImages}
        />

        <Spacer size={[2, 4]} />
      </Layout>
    </LifeStoriesContextProvider>
  )
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const singingPortraits = await getContentfulSingingPortraits()
  const mediaMentions = await getContentfulMediaMentions('global')
  const faq = await getContentfulFAQ('singing-portraits')

  return {
    props: {
      ...(await getIntlProps(ctx, ['life_stories', 'memory'])),
      singingPortraits,
      mediaMentions,
      faq,
    },
    revalidate: REVALIDATE_SECS,
  }
}
